import { Paper, Typography, useTheme, Skeleton, Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import React, { useState } from "react";
import { ClickableGridCell } from "../../../../../components/ClickableGridCell";
import { DataGrid } from "../../../../../components/DataGrid";
import { ChemicalFacilitiesSummaryQuery } from "../../../../../generated-graphql/graphql";
import { AssignmentManagementTableFilterKeys } from "../../AssignmentManagerTable/useAssignmentManagerOmnisearch";

interface UnverifiedTableProps {
  type: "Organization" | "State";
  data?: ChemicalFacilitiesSummaryQuery["reportingSeasonSummary"]["organizationSummaries"];
  loading: boolean;
  hideRowDividers?: boolean;
  title?: string;
}

export const UnverifiedTable: React.FC<UnverifiedTableProps> = ({
  type = "Organization",
  data = [],
  loading,
  hideRowDividers = false,
  title,
}) => {
  const theme = useTheme();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 8,
    page: 0,
  });
  const { createUrlFromOmnisearchKeyValues } = useOmnisearchDatagrid({
    persistenceKey: "unverified-table",
  });

  const columns: GridColDef[] = [
    { field: "name", headerName: type, flex: 1 },
    {
      field: "unverifiedCount",
      headerName: "Unverified",
      flex: 1,
      type: "number",
      sortable: true,
      renderCell: (params) => {
        return (
          <ClickableGridCell
            href={createUrlFromOmnisearchKeyValues({
              keyValues: [
                [AssignmentManagementTableFilterKeys.Unverified, "true"],
                [
                  AssignmentManagementTableFilterKeys[type],
                  params.row.name.includes(" ")
                    ? `"${params.row.name}"`
                    : params.row.name,
                ],
              ],
              keysToKeep: ["reportingYear", "reportKind"],
            })}
          >
            {params.value}
          </ClickableGridCell>
        );
      },
    },
    {
      field: "reportCount",
      headerName: "# of Reporting Facilities",
      flex: 1,
      type: "number",
      sortable: true,
      renderCell: (params) => {
        return (
          <ClickableGridCell
            href={createUrlFromOmnisearchKeyValues({
              keyValues: [
                [
                  AssignmentManagementTableFilterKeys[type],
                  params.row.name.includes(" ")
                    ? `"${params.row.name}"`
                    : params.row.name,
                ],
              ],
              keysToKeep: ["reportingYear", "reportKind"],
            })}
          >
            {params.value}
          </ClickableGridCell>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Paper
        sx={{
          height: "100%",
          width: "100%",
          p: 3,
          marginBottom: 1,
          animation: "fadeIn 0.3s ease-in-out",
          "@keyframes fadeIn": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
        elevation={0}
      >
        {title && (
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            {title}
          </Typography>
        )}
        <Box sx={{ mt: title ? 2 : 0 }}>
          {/* Header skeleton */}
          <Box
            sx={{
              display: "flex",
              mb: 1,
              height: 40,
              animation: "slideIn 0.3s ease-out 0.1s both",
              "@keyframes slideIn": {
                from: {
                  opacity: 0,
                  transform: "translateY(10px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            <Skeleton
              variant="rectangular"
              width="50%"
              height={40}
              sx={{ mr: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width="25%"
              height={40}
              sx={{ mr: 1 }}
            />
            <Skeleton variant="rectangular" width="25%" height={40} />
          </Box>
          {/* Row skeletons */}
          {Array.from({ length: 7 }).map((_, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                mb: 0.5,
                height: 38,
                animation: `slideIn 0.3s ease-out ${(index + 2) * 0.05}s both`,
                "@keyframes slideIn": {
                  from: {
                    opacity: 0,
                    transform: "translateY(10px)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              <Skeleton
                variant="rectangular"
                width="50%"
                height={32}
                sx={{ mr: 1 }}
              />
              <Skeleton
                variant="rectangular"
                width="25%"
                height={32}
                sx={{ mr: 1 }}
              />
              <Skeleton variant="rectangular" width="25%" height={32} />
            </Box>
          ))}
          {/* Pagination skeleton */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 1,
              height: 52,
              animation: "slideIn 0.3s ease-out 0.6s both",
              "@keyframes slideIn": {
                from: {
                  opacity: 0,
                  transform: "translateY(10px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            <Skeleton variant="rectangular" width={200} height={32} />
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        height: "100%",
        width: "100%",
        p: 3,
        marginBottom: 1,
        animation: "fadeIn 0.5s ease-in-out",
        "@keyframes fadeIn": {
          from: { opacity: 0, transform: "translateY(10px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
      elevation={0}
    >
      {title && (
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
          }}
        >
          {title}
        </Typography>
      )}
      <DataGrid
        rows={data}
        columns={columns}
        loading={false}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "unverifiedCount",
                sort: "desc",
              },
            ],
          },
        }}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => {
          setPaginationModel(model);
        }}
        pagination
        sx={{
          border: "none",
          opacity: 1,
          transition: "opacity 0.3s ease-in-out",
          ...(hideRowDividers && {
            "& .MuiDataGrid-cell": {
              border: "none",
            },
          }),
        }}
      />
    </Paper>
  );
};
